import { Flex } from 'reflexbox';
import {
  Input, Button,
} from '~/components';
import {
  Card, Title, CustomInput, Label, BtnClean, StyledLink,
} from './styles';
import { SelectDMTport } from 'components/SelectDmtPort';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStateVar } from '~/helpers/useStateVar';
import { ApiResps, apiCall } from '~/providers';
import { toast } from 'react-toastify';
import { useEffect } from 'react';
import SelectSearch, { fuzzySearch } from 'react-select-search';
import { propOrNullValue } from '~/pages/ClientPanel/FormEditUtility';
import { verifyInfoAdditionalParameters } from '~/helpers/additionalParameters';
import { typesUtilities } from '../../DMTs/AcessorUtilities';

export const UtilityEdit = ({ utilInfo, getUtilityInfo }): JSX.Element => {
  const match = useRouteMatch<{ utilId: string, type: string }>();
  const history = useHistory();
  const { t } = useTranslation();
  const [state, render, setState] = useStateVar({
    linkBase: match.url.split(`/${match.params.utilId}`)[0],
    isLoading: false,
    utilInfo: utilInfo as (ApiResps['/dmt/get-utility-info']),
    ports: [] as {
      label: string,
      associated: boolean,
      port: number,
      nobreakId?: number,
    }[],
    selectedPort: null as null|number,
    selectedFeedback: null as null|string,
    sensors: [] as {
      name: string;
      value: string;
    }[],
    models: [] as {
      name: string;
      value: number;
    }[],
    modelsIdentifier: {} as Record<string, { MODEL_NAME: string, INFO: { ID: number; FIELD_NAME: string; VALUE: string; MEASURE_UNIT: string; TYPE_FIELD: string }[] }>,
  });
  const [formData, _, setForm] = useStateVar({
    PORT: state.utilInfo?.PORT || null,
    PORT2nd: state.utilInfo?.PORT2nd || null,
    MANUFACTURER: state.utilInfo?.MANUFACTURER || '',
    MODEL: state.utilInfo?.UTILITY_MODEL_ID || '' as any,
    SENSOR: (state.utilInfo?.SENSOR && state.utilInfo?.SENSOR.toString()) || null,
  });
  const { utilId } = match.params;

  async function getDmtPortsInfo(utilInfo) {
    if (utilInfo?.DMT_CODE) {
      const portsInfo = await apiCall('/dmt/get-dmt-ports-info', { DMT_CODE: utilInfo.DMT_CODE, CLIENT_ID: utilInfo.CLIENT_ID, NEW_UTILITY_TYPE: 'Nobreak' });
      state.ports = (isElevatorOrEscalator ? portsInfo.portsAnalogics : portsInfo.portsDigitals).map((port) => { if (port.utilityId === utilInfo.NOBREAK_ID) { port.associated = false; } return port; });
    }
  }

  function updateForm(utilInfo) {
    setForm({
      PORT: propOrNullValue(utilInfo.PORT),
      MANUFACTURER: utilInfo?.MANUFACTURER || '',
      MODEL: state.utilInfo?.UTILITY_MODEL_ID || '' as any,
      SENSOR: utilInfo?.SENSOR || null,
    });
  }

  const backToLastRoute = () => {
    // @ts-ignore
    if (history.location.state && history.location.state.from === 'dmtInfo' && state.utilInfo.DMT_CODE) {
      history.push(`/analise/dispositivo/${state.utilInfo.DMT_CODE}/informacoes`);
    } else {
      history.push(`${state.linkBase}/${utilId}/informacoes`);
    }
  };

  async function confirmEditUtil() {
    try {
      setState({ isLoading: true });
      await apiCall('/dmt/set-dmt-nobreak', {
        ID: state.utilInfo.NOBREAK_ID,
        DMT_CODE: state.utilInfo.DMT_CODE,
        UNIT_ID: state.utilInfo.UNIT_ID,
        NAME: state.utilInfo.NAME,
        MANUFACTURER: propOrNullValue(formData.MANUFACTURER),
        MODEL: propOrNullValue(state.modelsIdentifier[formData.MODEL]?.MODEL_NAME),
        SENSOR: propOrNullValue(formData.SENSOR),
        PORT: propOrNullValue(formData.PORT),
        PORT2: formData.PORT2nd ? formData.PORT2nd : undefined,
        TYPE: typesUtilities[match.params.type],
      });
      await getUtilityInfo();
      toast.success(t('sucessoSalvar'));
      backToLastRoute();
    } catch (err) {
      console.log(err);
      toast.error(t('houveErro'));
    }
    setState({ isLoading: false });
  }

  async function getExtraUtilityInfo() {
    if (utilInfo) {
      try {
        setState({ isLoading: true });
        updateForm(utilInfo);
        await getDmtPortsInfo(utilInfo);
      } catch (err) {
        console.log(err);
        toast.error(t('houveErro'));
      }
      setState({ isLoading: false });
    }
  }

  async function getSensorsInfo() {
    if (!utilInfo?.DMT_CODE?.startsWith('DMT2')) return;
    try {
      const { list } = await apiCall('/config/get-pressure-sensors');
      setState({
        ...state,
        sensors: list.map((item) => ({ name: item.SENSOR_ID, value: item.SENSOR_ID })),
      });
    } catch (err) {
      toast.error(t('naoFoiPossivelBuscarSensores'));
    }
  }

  async function getModelsInfo() {
    try {
      const listModels = await apiCall('/utilities/models');
      const modelsDescs = {} as Record<string, { MODEL_NAME: string, INFO: { ID: number; FIELD_NAME: string; VALUE: string; MEASURE_UNIT: string; TYPE_FIELD: string }[] }>;
      (listModels[typesUtilities[match.params.type]] || []).forEach((model) => {
        if (!modelsDescs[model.value]) {
          modelsDescs[model.value] = {
            MODEL_NAME: model.name,
            INFO: [...model.info],
          };
        } else {
          modelsDescs[model.value].INFO.push(...model.info);
        }
      });
      setState({
        ...state,
        models: listModels[typesUtilities[match.params.type]] || [],
        modelsIdentifier: modelsDescs,
      });
    } catch (err) {
      console.log(err);
      toast.error(t('naoFoiPossivelBuscarModelos'));
    }
  }
  useEffect(() => {
    getExtraUtilityInfo();
    getSensorsInfo();
    getModelsInfo();
  }, []);
  const isElevatorOrEscalator = (match.params.type === 'elevator' || match.params.type === 'escalator');
  const isDMT2 = utilInfo?.DMT_CODE?.startsWith('DMT2');
  return (
    <Card style={{ borderTop: '10px solid #363BC4' }}>
      <Flex width="100%" flexDirection="column" flex="wrap" padding={20}>
        <Flex style={{ width: '100%', alignItems: 'center', justifyContent: 'space-between' }}>
          <Flex flexDirection="column">
            <span style={{ fontSize: '12px', fontWeight: 700, lineHeight: '14px' }}>{`${t('editar')} ${t('utilitario')}`}</span>
            <span style={{ fontSize: '15px', fontWeight: 700 }}>{utilInfo?.NAME}</span>
            <span style={{ fontWeight: 500, fontSize: '11px' }}>
              {(utilInfo?.DMT_CODE)
            && (
              <StyledLink to={`/analise/dispositivo/${utilInfo?.DMT_CODE}/informacoes`}>
                {utilInfo?.DMT_CODE || '-'}
              </StyledLink>
            )}
              { !utilInfo?.DMT_CODE
            && (
              <>
                -
              </>
            )}
            </span>
          </Flex>
        </Flex>

        {!state.isLoading
        && (
        <>
          <Flex flexDirection="column" mt={40}>
            <Title>{t('informacoes')}</Title>
            <Flex flexDirection="column" marginTop="5px">
              <div style={{ width: '25%', minWidth: '200px' }}>
                <Input
                  type="text"
                  value={formData.MANUFACTURER}
                  label={t('fabricante')}
                  placeholder={t('digitar')}
                  onChange={(event) => { formData.MANUFACTURER = event.target.value; render(); }}
                />
              </div>
              <div style={{ width: '25%', minWidth: '200px', marginTop: '20px' }}>
                <CustomInput style={{ width: '100%' }}>
                  <div style={{ paddingTop: 3, width: '100%' }}>
                    <Label>{t('modelo')}</Label>
                    <SelectSearch
                      onChange={(item) => { formData.MODEL = item; render(); }}
                      closeOnSelect
                      value={formData.MODEL}
                      placeholder={t('selecioneTensao')}
                      options={state.models}
                    />
                  </div>
                </CustomInput>
              </div>
              <Flex flexDirection="column" flexWrap="wrap" justifyContent="flex-start" width="250px" alignItems="flex-start">
                {state.modelsIdentifier[formData.MODEL]?.INFO.length > 0 && verifyInfoAdditionalParameters([], false, state.modelsIdentifier[formData.MODEL]?.INFO)}
              </Flex>
            </Flex>
            {utilInfo.DMT_CODE && (
              <>
                <Title>{t('associacao')}</Title>
                <Flex flexWrap="wrap" justifyContent="space-between">
                  <div style={{ minWidth: '150px', width: '25%' }}>
                    <SelectDMTport
                      label={isElevatorOrEscalator ? t('porta') : t('feedback')}
                      placeholder={t('selecionar')}
                      options={state.ports}
                      propLabel="label"
                      value={(formData.PORT ? isElevatorOrEscalator ? `P${formData.PORT - 1}` : `F${formData.PORT}` : '')}
                      hideSelected
                      onSelect={(item) => {
                        formData.PORT = item.port;
                        render();
                      }}
                    />
                  </div>
                </Flex>
                <BtnClean onClick={() => { formData.PORT = null; render(); }}>{t('limparPorta')}</BtnClean>
                {
                  match.params.type === 'generator' && (
                    <Flex flexDirection="column">
                      <div style={{ minWidth: '150px', width: '25%' }}>
                        <SelectDMTport
                          label={t('feedbackCargaLoja')}
                          placeholder={t('selecionar')}
                          options={state.ports}
                          propLabel="label"
                          value={(formData.PORT2nd ? `F${formData.PORT2nd}` : '')}
                          hideSelected
                          onSelect={(item) => {
                            formData.PORT2nd = item.port;
                            render();
                          }}
                        />
                      </div>
                      <BtnClean onClick={() => { formData.PORT2nd = null; render(); }}>{t('limparPorta')}</BtnClean>
                    </Flex>
                  )
                }
                { (isDMT2 && isElevatorOrEscalator) && (
                  <Flex flexDirection="column" style={{ width: '25%', minWidth: '200px', marginRight: 0 }}>
                    <CustomInput style={{ width: '100%' }}>
                      <div style={{ width: '100%' }}>
                        <Label>{t('sensorCorrente')}</Label>
                        <SelectSearch
                          placeholder={t('selecionar')}
                          options={state.sensors}
                          propLabel="name"
                          value={formData.SENSOR || ''}
                          hideSelected
                          search
                          filterOptions={fuzzySearch}
                          onChange={(item) => {
                            formData.SENSOR = item as any;
                            render();
                          }}
                        />
                      </div>
                    </CustomInput>
                    <BtnClean onClick={() => { formData.SENSOR = null; render(); }}>{t('limparSensor')}</BtnClean>
                  </Flex>
                )}
              </>
            )}
          </Flex>

          <Flex justifyContent="space-between" alignItems="center" marginTop="40px">
            <Button
              style={{ width: '100px' }}
              onClick={confirmEditUtil}
              variant="primary"
            >
              {`${t('salvar')}`}
            </Button>
            <BtnClean onClick={backToLastRoute}>{t('cancelar')}</BtnClean>
          </Flex>
        </>
        )}
      </Flex>
    </Card>
  );
};
